import { FormBuilder } from '@act/storybook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { useMemo } from 'react';
import { TrackingProvider } from 'react-components/tracking';
import { Control, FormState } from 'react-hook-form';
import STATE_OPTIONS from 'redux/constants/stateOptions';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { themeColors } from 'toolkit/styles/colors';
import {
  ActionFormResourceDetails,
  ActionFormValues,
} from '../components/useNeedActionFormtypes';

interface ResourceReferralFormProps {
  control: Control<ActionFormValues>;
  details: ActionFormResourceDetails;
  resourceDetailsIndex: number;
  formState: FormState<ActionFormValues>;
  expanded: boolean;
  expandResourceForm: () => void;
}

const ResourceReferralForm = ({
  control,
  details,
  resourceDetailsIndex,
  formState,
  expanded,
  expandResourceForm,
}: ResourceReferralFormProps) => {
  const stateOptions = STATE_OPTIONS;

  const formConfig = useMemo(
    () =>
      [
        {
          type: 'input',
          name: `resourceDetails.${resourceDetailsIndex}.resourceName`,
          label: 'Resource name*',
          input: 'text',
          props: {
            trackingName: 'resource-name-input',
            required: true,
            fullWidth: true,
          },
        },
        {
          type: 'input',
          name: `resourceDetails.${resourceDetailsIndex}.contactName`,
          label: 'Contact person',
          input: 'text',
          props: {
            trackingName: 'contact-name-input',
            fullWidth: true,
          },
        },
        {
          type: 'group',
          items: [
            {
              type: 'input',
              name: `resourceDetails.${resourceDetailsIndex}.phoneNumber`,
              label: 'Phone number',
              input: 'phone',
              props: {
                fullWidth: true,
                trackingName: 'resource-phone-input',
              },
            },
            {
              type: 'input',
              name: `resourceDetails.${resourceDetailsIndex}.url`,
              label: 'URL',
              input: 'text',
              props: {
                trackingName: 'resource-url-input',
                fullWidth: true,
              },
            },
          ],
        },
        {
          type: 'input',
          name: `resourceDetails.${resourceDetailsIndex}.streetAddress`,
          label: 'Street address',
          input: 'text',
          props: {
            trackingName: 'street-address-input',
            fullWidth: true,
          },
        },
        {
          type: 'input',
          name: `resourceDetails.${resourceDetailsIndex}.city`,
          label: 'City',
          input: 'text',
          props: {
            trackingName: 'city-input',
            fullWidth: true,
          },
        },
        {
          type: 'group',
          items: [
            {
              type: 'input',
              name: `resourceDetails.${resourceDetailsIndex}.state`,
              label: 'State',
              input: 'select',
              props: {
                fullWidth: true,
                trackingName: 'state-select',
                options: Object.entries(stateOptions).map(([label, value]) => ({
                  key: label.concat('-', value),
                  label: `${label} - ${value}`,
                  value,
                })),
              },
            },
            {
              type: 'input',
              name: `resourceDetails.${resourceDetailsIndex}.zipcode`,
              label: 'Zip code',
              input: 'text',
              props: {
                trackingName: 'zipcode-input',
                fullWidth: true,
                characterLimit: 5,
                hideCharacterCount: true,
              },
            },
          ],
        },
      ] as FormConfig<ActionFormValues>,
    [resourceDetailsIndex, stateOptions]
  );

  return (
    <TrackingProvider contextName="resource-referral-form">
      <Accordion expanded={expanded} onChange={expandResourceForm}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-label="Non-focused resource"
          sx={{ ...(details.shouldDelete && { color: themeColors.error }) }}
        >
          {`${details?.resourceName}${
            details.shouldDelete ? ' - will be deleted' : ''
          }` || 'New resource'}
        </AccordionSummary>
        <AccordionDetails>
          <FormBuilder
            config={formConfig}
            control={control}
            formState={formState}
          />
        </AccordionDetails>
      </Accordion>
    </TrackingProvider>
  );
};

export default ResourceReferralForm;
